import React, { useEffect, useId, useRef } from 'react';
import '../styles/ConfirmationModal.scss';
import { FaExclamationTriangle, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string | React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    type?: 'warning' | 'danger' | 'info';
    isConfirming?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 onConfirm,
                                                                 title,
                                                                 message,
                                                                 confirmText = "Confirm",
                                                                 cancelText = "Cancel",
                                                                 type = 'info',
                                                                 isConfirming = false,
                                                             }) => {

    const dialogRef = useRef<HTMLDivElement>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const onCloseRef = useRef(onClose);
    const isConfirmingRef = useRef(isConfirming);
    const titleId = useId();
    const messageId = useId();

    onCloseRef.current = onClose;
    isConfirmingRef.current = isConfirming;

    const getIcon = () => {
        switch (type) {
            case 'warning': return <FaExclamationTriangle />;
            case 'danger': return <FaExclamationCircle />;
            case 'info':
            default: return <FaInfoCircle />;
        }
    };

    const icon = getIcon();

    useEffect(() => {
        if (!isOpen) return;

        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const frame = window.requestAnimationFrame(() => {
            const cancelButton = cancelButtonRef.current;
            if (cancelButton && !cancelButton.disabled) {
                cancelButton.focus({ preventScroll: true });
            } else {
                dialogRef.current?.focus({ preventScroll: true });
            }
        });

        const handleEsc = (e: KeyboardEvent) => {
            // This dialog may be opened from inside another modal. Capture these
            // keys before the underlying modal's document handler sees them.
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                if (!isConfirmingRef.current) onCloseRef.current();
                return;
            }

            if (e.key !== 'Tab' || !dialogRef.current) return;
            e.stopPropagation();

            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter((element) => element.getClientRects().length > 0);
            if (focusable.length === 0) {
                e.preventDefault();
                dialogRef.current.focus({ preventScroll: true });
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeElement = document.activeElement as HTMLElement | null;
            if (!activeElement || !focusable.includes(activeElement)) {
                e.preventDefault();
                (e.shiftKey ? last : first).focus();
            } else if (e.shiftKey && activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleEsc, true);
        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener('keydown', handleEsc, true);
            if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="confirmation-modal-overlay">
            <div
                ref={dialogRef}
                className={`confirmation-modal ${type}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={messageId}
                aria-busy={isConfirming || undefined}
                tabIndex={-1}
            >
                <div className="confirmation-header">
                    <h2 id={titleId}>{title}</h2>
                    <button
                        type="button"
                        className="confirmation-close"
                        onClick={onClose}
                        aria-label="Close confirmation modal"
                        disabled={isConfirming}
                    >
                        &times;
                    </button>
                </div>
                <div className="confirmation-body">
                    <div className="confirmation-icon">{icon}</div>
                    <div id={messageId} className="confirmation-message">{message}</div>
                    <div className="confirmation-actions">
                        <button
                            ref={cancelButtonRef}
                            type="button"
                            className="cancel-button"
                            onClick={onClose}
                            disabled={isConfirming}
                        >
                            {cancelText}
                        </button>
                        <button
                            type="button"
                            className={`confirm-button ${type}`}
                            onClick={onConfirm}
                            disabled={isConfirming}
                        >
                            {isConfirming ? 'Processing...' : confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
